import { useState } from 'react';
import type { ChangeEvent, FormEvent } from 'react';
import { signInWithEmailAndPassword, signOut } from 'firebase/auth';
import { Box, Button, TextField, Typography, Paper, Alert } from '@mui/material';
import { auth } from '../firebase';
import { useTenant } from '../providers/TenantProvider';

export const AuthPage = () => {
  const { user, tenantId, role, tenants, refreshTenants } = useTenant();
  const [email, setEmail] = useState('owner@demogym.com');
  const [password, setPassword] = useState('demo1234');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [message, setMessage] = useState<string | null>(null);

  const handleSignIn = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setLoading(true);
    setError(null);
    setMessage(null);

    try {
      const userCredential = await signInWithEmailAndPassword(auth, email, password);
      console.log('Signed in:', userCredential.user.email);

      await refreshTenants();

      setMessage('Successfully signed in!');
    } catch (err: any) {
      console.error('Sign in error:', err);
      setError(err.message || 'Failed to sign in');
    } finally {
      setLoading(false);
    }
  };

  const handleSignOut = async () => {
    try {
      await signOut(auth);
      setMessage('Successfully signed out');
    } catch (err: any) {
      setError(err.message || 'Failed to sign out');
    }
  };

  const handleRefreshClaims = async () => {
    setLoading(true);
    setError(null);
    setMessage(null);

    try {
      await refreshTenants();
      setMessage('Tenant memberships refreshed successfully!');
    } catch (err: any) {
      setError(err.message || 'Failed to refresh tenant memberships');
    } finally {
      setLoading(false);
    }
  };

  if (user) {
    return (
      <Box
        sx={{
          minHeight: '100vh',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          bgcolor: 'grey.100',
          p: 2,
        }}
      >
        <Paper sx={{ p: 4, maxWidth: 600, width: '100%' }}>
          <Typography variant="h4" gutterBottom>
            Welcome!
          </Typography>

          <Box sx={{ my: 3 }}>
            <Typography variant="body1" gutterBottom>
              <Box component="strong" sx={{ fontWeight: 700 }}>Email:</Box> {user.email}
            </Typography>
            <Typography variant="body1" gutterBottom>
              <Box component="strong" sx={{ fontWeight: 700 }}>UID:</Box> {user.uid}
            </Typography>
            <Typography variant="body1" gutterBottom>
              <Box component="strong" sx={{ fontWeight: 700 }}>Active Tenant ID:</Box> {tenantId || 'Not set'}
            </Typography>
            <Typography variant="body1" gutterBottom>
              <Box component="strong" sx={{ fontWeight: 700 }}>Role:</Box> {role || 'Not set'}
            </Typography>
            <Typography variant="body1" gutterBottom>
              <Box component="strong" sx={{ fontWeight: 700 }}>Available Tenants:</Box> {tenants.length}
            </Typography>
          </Box>

          {error && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
          )}

          {message && (
            <Alert severity="success" sx={{ mb: 2 }}>
              <Box component="pre" sx={{ m: 0, whiteSpace: 'pre-wrap' }}>
                {message}
              </Box>
            </Alert>
          )}

          {!tenantId && (
            <Alert severity="warning" sx={{ mb: 2 }}>
              No tenant memberships found. Run the seed script or ask an owner to add this email to a tenant.
            </Alert>
          )}

          <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
            <Button
              variant="outlined"
              onClick={handleRefreshClaims}
              disabled={loading}
            >
              Refresh Tenants
            </Button>
            <Button variant="contained" color="error" onClick={handleSignOut}>
              Sign Out
            </Button>
          </Box>
        </Paper>
      </Box>
    );
  }

  return (
    <Box
      sx={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        backgroundImage: 'url(/bg.jpg)',
        backgroundRepeat: 'repeat',
        p: 2,
      }}
    >
      <Paper sx={{ p: 4, maxWidth: 400, width: '100%' }}>
        <Box
          component="img"
          src="/logo.png"
          alt="BullPlanner CRM"
          sx={{
            display: 'block',
            width: 180,
            maxWidth: '72%',
            height: 'auto',
            mx: 'auto',
            mb: 3,
          }}
        />

        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        {message && (
          <Alert severity="success" sx={{ mb: 2 }}>
            {message}
          </Alert>
        )}

        <Box component="form" onSubmit={handleSignIn}>
          <TextField
            fullWidth
            label="Email"
            type="email"
            value={email}
            onChange={(e: ChangeEvent<HTMLInputElement>) => setEmail(e.target.value)}
            margin="normal"
            required
          />
          <TextField
            fullWidth
            label="Password"
            type="password"
            value={password}
            onChange={(e: ChangeEvent<HTMLInputElement>) => setPassword(e.target.value)}
            margin="normal"
            required
          />
          <Button
            fullWidth
            variant="contained"
            type="submit"
            disabled={loading}
            sx={{ mt: 2 }}
          >
            {loading ? 'Signing in...' : 'Sign In'}
          </Button>
        </Box>
      </Paper>
    </Box>
  );
};
